import { useState, useEffect } from 'react';
import { ChevronRight, Timer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useArenaStore } from '../../store/arena-store';
import { effectiveEndsAt, POSTO_MUTO } from '../../utils/tournament-helpers';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useArenaPartita } from './useArenaPartita';
import './ArenaHeaderCells.css';

/**
 * Il tempo che resta all'arena, la cella che chi gioca ha nell'intestazione: rossa
 * sotto i cinque minuti. Fuori da `ArenaHeaderCells` perche' la usa anche chi
 * GUARDA una partita d'arena, che non ha lo store dell'arena (SpectateGame).
 */
export function CellaTempoArena({ fineArena }: { fineArena: string | null }) {
  const { t } = useTranslation();
  const [arenaTime, setArenaTime] = useState('');
  const [isTimeLow, setIsTimeLow] = useState(false);

  useEffect(() => {
    if (!fineArena) return;

    const updateTimer = (): void => {
      const diff = new Date(fineArena).getTime() - Date.now();

      if (diff <= 0) {
        setArenaTime(t('tournament.timer.finished'));
        setIsTimeLow(false);
        return;
      }

      setIsTimeLow(diff < 5 * 60 * 1000);

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setArenaTime(
        hours > 0
          ? `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
          : `${minutes}:${seconds.toString().padStart(2, '0')}`
      );
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [fineArena, t]);

  return (
    <span className="game-header__cell" data-testid="arena-header-time-cell">
      <Timer size={14} aria-hidden="true" />
      <span
        className={`arena-header-time${isTimeLow ? ' arena-header-time--low' : ''}`}
        data-testid="arena-header-time"
        data-time-low={isTimeLow ? 'true' : undefined}
      >
        {arenaTime}
      </span>
    </span>
  );
}

/**
 * A che punto sei nell'arena, dentro la riga del `GameHeader`.
 *
 * Non è un blocco che vive per conto suo, ed è la ragione del nome: prima era
 * una striscia larga 500px appesa a `headerSlot`, cioè fuori dalla griglia.
 * Là dentro era centrata sulla pagina mentre la damiera è centrata nella sua
 * colonna — con la barra laterale da 340px le due cose non coincidono, e si
 * vedeva. Peggio: stando fuori dal flusso, la sua altezza andava scalata a
 * mano dalla damiera con due `calc` di numeri misurati a occhio, e `.game-screen`
 * taglia (non scrolla) quello che non entra.
 *
 * Qui invece sono celle come le altre: stessa riga, stessa larghezza della
 * colonna, nessuna altezza in più da compensare.
 *
 * La fiamma della serie non c'è: quell'informazione sta già in classifica — e
 * adesso la classifica si apre da qui, premendo il punteggio.
 */
interface ArenaHeaderCellsProps {
  /**
   * Apre la classifica dell'arena. Con questa, il punteggio è un bottone;
   * senza, resta la scritta che era — così la riga regge anche un ospite che
   * non ha niente da aprire, e il `memo` di chi la monta confronta comunque un
   * riferimento stabile (la stessa callback, oppure niente).
   */
  onApriClassifica?: () => void;
}

export default function ArenaHeaderCells({ onApriClassifica }: ArenaHeaderCellsProps = {}) {
  const { t } = useTranslation();
  /* Serve a una cosa sola: dire se il bersaglio del bottone è un `dialog` (il
     pannello a tutto schermo) o la scheda della colonna laterale, che non lo è.
     È lo stesso valore su cui `ArenaGame` decide quale dei due aprire. */
  const isMobile = useIsMobile();

  /*
   * Il conto alla rovescia dice quanto manca alla fine dell'arena in cui si
   * sta GIOCANDO — e' il numero da cui si capisce se c'e' tempo per un'altra
   * partita. Leggeva `currentArena`, cioe' l'arena che si sta guardando: chi
   * era in coda in A e apriva B vedeva il tempo di B. Vedi `useArenaPartita`.
   */
  const arenaPartita = useArenaPartita();
  const myParticipant = useArenaStore((s) => s.myParticipant);
  const myRank = useArenaStore((s) => s.myRank);

  /*
   * ALLA PRIMA PARTITA il posto non c'e' ancora.
   *
   * Il segnale sono le PARTITE di chi guarda, e non la classifica: chi non ha
   * ancora chiuso una partita non ha un posto da leggere. Fra pari punti
   * l'arena numera per `user_id`, quindi quel numero sarebbe arbitrario
   * comunque. E' un'approssimazione per DIFETTO: anche chi entra a meta' arena
   * vede il trattino nella sua prima partita. Vedi `nessunoHaGiocato` per la
   * regola intera.
   *
   * Resta su `gamesPlayed` anche ora che la classifica dell'arena giocata c'e'
   * (`standingsPartita`): questa cella la disegna anche chi non la legge, e un
   * conto di partite e' piu' economico e dice la stessa cosa.
   */
  const postoMuto = myParticipant?.gamesPlayed === 0;

  /* La fine può non essere ancora scritta: vedi `effectiveEndsAt`. */
  const fineArena = arenaPartita ? effectiveEndsAt(arenaPartita) : null;

  /* Gli stessi due pezzi di testo per tutt'e due i rami, e per il nome
     accessibile del bottone: scritti una volta, o le tre copie divergono. */
  const posto = postoMuto || !myRank ? POSTO_MUTO : `#${myRank}`;
  const punti = `${myParticipant?.points || 0} ${t('tournament.lobby.pts')}`;

  return (
    <>
      <CellaTempoArena fineArena={fineArena} />
      <span className="game-header__divider" aria-hidden="true" />
      {onApriClassifica ? (
        /*
         * IL PUNTEGGIO E' LA PORTA DELLA CLASSIFICA.
         *
         * Dove la domanda «come sto andando?» ha gia' mezza risposta, ed e' la
         * strada che il torneo ha gia' fatto (`TournamentGame`). Non e' un
         * quarto bottone nella barra in basso perche' a 360px quella barra e'
         * piena — Mosse, tre frecce, Chat, occhio.
         *
         * Un bottone vestito come la scritta che era: stesso colore, stesso
         * carattere. La freccetta dice che si tocca; i 44px d'area di tocco li
         * da' `--tap` (vedi GameHeader.css), che ALZA la riga invece di farli
         * sporgere fuori dal suo ritaglio. Il nome accessibile CONTIENE il
         * testo visibile, cosi' chi comanda a voce «tocca #4» lo trova.
         */
        <button
          type="button"
          className="game-header__cell game-header__cell--tap game-header__cell--apri arena-header-standing-apri"
          onClick={onApriClassifica}
          aria-label={`${t('tournament.standings.title')}: ${posto} · ${punti}`}
          aria-haspopup={isMobile ? 'dialog' : undefined}
          data-testid="arena-game-open-standings"
        >
          <span className="arena-header-rank" data-testid="arena-header-rank">{posto}</span>
          <span className="arena-header-points" data-testid="arena-header-points">{punti}</span>
          <ChevronRight size={14} aria-hidden="true" className="arena-header-icon" />
        </button>
      ) : (
        <span className="game-header__cell">
          <span className="arena-header-rank" data-testid="arena-header-rank">{posto}</span>
          <span className="arena-header-points" data-testid="arena-header-points">{punti}</span>
        </span>
      )}
    </>
  );
}
